import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { FolderOutline } from '@lyearn/icons';
import { Radio, Stack, Typography, useSnackbar } from '@lyearn/ui';
import { IconSettings } from '@/components/IconSettings';
import { Modal } from '@/components/Modals';
import useResponse from '@/hooks/useResponse';
import {
  FILE_SEARCH_STACK_VARIABLES,
  useFileSearch,
} from '@/modules/tasks/features/actionItems/hooks/useFileSearch';
import { ArrayOperationCommandEnum } from '@/types/schema';

import useFileActions from '../../../hooks/useFileActions';
import useFileMutations from '../../../hooks/useFileMutations';
import { DialogProps } from '../types';

export default function MoveToSection({ onClose, fileId }: DialogProps) {
  const { state } = useFileActions({ onSuccess: onClose });
  const { t } = useTranslation();

  const [isFetching, setIsFetching] = useState(false);

  const { files: sections, isFetching: isFetchingStacks } = useFileSearch({
    pageSize: -1,
    filters: FILE_SEARCH_STACK_VARIABLES,
  });

  const currentSelectedSection = useMemo(() => {
    return sections?.find((section) => section.childrenIds?.includes(fileId!));
  }, [fileId, sections]);

  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
    currentSelectedSection?.id ?? null,
  );

  const { updateFile } = useFileMutations();

  const { showSnackbar } = useSnackbar();
  const { handleResponse } = useResponse();

  const onSubmit = useCallback(async () => {
    if (!selectedSectionId || !fileId) return;

    const selectedSection = sections?.find((section) => section.id === selectedSectionId);
    setIsFetching(true);

    const [addToNewSectionData] = await Promise.all([
      updateFile({
        data: {
          fileId: selectedSectionId,
          children: {
            values: [fileId],
            operation: [ArrayOperationCommandEnum.Add],
            position: 0,
          },
        },
      }),

      currentSelectedSection?.id
        ? updateFile({
            data: {
              fileId: currentSelectedSection.id,
              children: {
                values: [fileId],
                operation: [ArrayOperationCommandEnum.Remove],
              },
            },
          })
        : Promise.resolve(),
    ]);

    handleResponse({
      condition: !addToNewSectionData.error,
      onSuccess: () => {
        showSnackbar({
          message: t('Task:Section.ListWasMoved', { name: selectedSection?.name }),
        });
        onClose();
      },
    });

    setIsFetching(false);
  }, [
    currentSelectedSection?.id,
    fileId,
    handleResponse,
    onClose,
    sections,
    selectedSectionId,
    showSnackbar,
    t,
    updateFile,
  ]);

  return (
    <Modal
      action={{
        disabled:
          isFetching ||
          state.isFetching ||
          isFetchingStacks ||
          !selectedSectionId ||
          selectedSectionId === currentSelectedSection?.id,
        isSubmitting: isFetching || state.isFetching || isFetchingStacks,
        text: t('Common:Actions.Move'),
        onSubmit: onSubmit,
      }}
      header={{
        title: t('Task:Section.MoveToSection'),
        description: t('Task:Section.MoveToSectionDescription'),
      }}
      onClose={onClose}>
      <Stack className="mx-24" direction="col">
        {!sections?.length ? (
          <Stack className="m-auto">
            <Stack alignItems="center" className="w-[200px] mt-[100px]" direction="col" gapY={8}>
              <FolderOutline fillColorToken="icon-placeholder" height={40} width={40} />
              <Typography className="text-text-placeholder text-center" variant="body-long-01">
                {t('Task:Section.EmptyState')}
              </Typography>
            </Stack>
          </Stack>
        ) : (
          sections?.map((section) => (
            <Stack
              key={section.id}
              className="border-b border-divider px-12 py-16 hover:bg-bg-secondary"
              direction="row"
              justifyContent="between"
              onClick={() => setSelectedSectionId(section.id)}>
              <Stack gapX={8}>
                {section.iconSettings ? (
                  <IconSettings
                    Icon={FolderOutline}
                    iconSettings={section.iconSettings}
                    size={24}
                  />
                ) : (
                  <FolderOutline fillColorToken="icon-primary" height={24} width={24} />
                )}
                <Typography variant="body-long-01">{section.name}</Typography>
              </Stack>
              <Radio
                checked={selectedSectionId === section.id}
                onChange={() => setSelectedSectionId(section.id)}
              />
            </Stack>
          ))
        )}
      </Stack>
    </Modal>
  );
}
